import { useState, useCallback, useMemo } from 'react';
import type {
    CreditAppSearchFormData,
    CreditAppSearchFormErrors,
    CreditAppSearchFormTouched,
    UseCreditAppSearchFormReturn
} from '../types';
import {
    validateLastName,
    validatePhone,
    validateEmail,
    isSearchButtonEnabled as checkIsSearchButtonEnabled
} from '../utils/validators';

const initialFormData: CreditAppSearchFormData = {
    lastName: '',
    phone: '',
    email: ''
};

const initialTouched: CreditAppSearchFormTouched = {
    lastName: false,
    phone: false,
    email: false
};

/**
 * Custom hook to manage credit app search form state
 * Encapsulates all form logic including validation, touched state, and form data
 */
export const useCreditAppSearchForm = (initialValues?: Partial<CreditAppSearchFormData>): UseCreditAppSearchFormReturn => {
    const [formData, setFormData] = useState<CreditAppSearchFormData>({
        ...initialFormData,
        ...initialValues
    });
    const [errors, setErrors] = useState<CreditAppSearchFormErrors>({});
    const [touched, setTouched] = useState<CreditAppSearchFormTouched>(initialTouched);

    /**
     * Handle field value changes
     */
    const handleFieldChange = useCallback(
        (field: keyof CreditAppSearchFormData, value: string) => {
            setFormData((prev) => ({
                ...prev,
                [field]: value
            }));

            // Clear error for this field when user starts typing
            if (errors[field]) {
                setErrors((prev) => {
                    const updated = { ...prev };
                    delete updated[field];
                    return updated;
                });
            }
        },
        [errors]
    );

    /**
     * Handle field blur events - mark field as touched and validate
     */
    const handleFieldBlur = useCallback(
        (field: keyof CreditAppSearchFormData) => {
            // Mark field as touched
            setTouched((prev) => ({
                ...prev,
                [field]: true
            }));

            // Validate the specific field
            const value = formData[field];
            let error: string | null = null;

            switch (field) {
                case 'lastName':
                    error = validateLastName(value);
                    break;
                case 'phone':
                    error = validatePhone(value);
                    break;
                case 'email':
                    error = validateEmail(value);
                    break;
            }

            // Update errors state
            if (error) {
                setErrors((prev) => ({
                    ...prev,
                    [field]: error
                }));
            } else {
                setErrors((prev) => {
                    const updated = { ...prev };
                    delete updated[field];
                    return updated;
                });
            }
        },
        [formData]
    );

    /**
     * Clear all form data, errors, and touched state
     */
    const clearForm = useCallback(() => {
        setFormData(initialFormData);
        setErrors({});
        setTouched(initialTouched);
    }, []);

    /**
     * Computed value for search button enabled state
     */
    const isSearchButtonEnabled = useMemo(() => {
        return checkIsSearchButtonEnabled(formData, errors);
    }, [formData, errors]);

    return {
        formData,
        errors,
        touched,
        handleFieldChange,
        handleFieldBlur,
        clearForm,
        isSearchButtonEnabled
    };
};
